
import { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import Logo from '../Logo';
import { useNavigate, useLocation } from 'react-router-dom';
import { useScrollEffect } from '../../hooks/useScrollEffect';
import { getCompleteNavItems } from '../../data/navItems';
import DesktopNavigation from './DesktopNavigation';
import MobileMenu from './MobileMenu';
import { NavItem } from '../../types/navigation';
import { useMemo } from 'react';

// Deklaracja rozszerzona dla obiektu window
declare global {
  interface Window {
    _navItemsBackup?: NavItem[];
  }
}

const Navbar = () => {
  // Zawsze używamy getCompleteNavItems aby zapewnić obecność stałych elementów
  const getInitialNavItems = useCallback(() => {
    // Użyj kopii zapasowej z window, jeśli jest dostępna
    if (window._navItemsBackup && window._navItemsBackup.length > 0) {
      console.log('Używam kopii zapasowej elementów nawigacji z window:', window._navItemsBackup);
      return [...window._navItemsBackup];
    }
    
    // W przeciwnym razie użyj getCompleteNavItems
    const items = getCompleteNavItems();
    
    // Dodatkowe sprawdzenie na wypadek problemów
    const hasBlog = items.some(item => item.href === '/blog');
    if (!hasBlog) {
      console.error('KRYTYCZNY BŁĄD: Brak zakładki Blog w nawigacji! Dodaję ręcznie...');
      items.push({ 
        label: 'Blog', 
        href: '/blog', 
        isInternal: true, 
        isPersistent: true 
      });
    }
    
    return items;
  }, []);
  
  // Pomocnicza funkcja do sprawdzenia, czy zakładka Blog jest obecna
  const ensureBlogExists = useCallback((items: NavItem[]): NavItem[] => {
    const hasBlog = items.some(item => item.href === '/blog');
    if (!hasBlog) {
      console.warn('Brak zakładki Blog! Dodaję ją...');
      const blogItem = { 
        label: 'Blog', 
        href: '/blog', 
        isInternal: true, 
        isPersistent: true 
      };
      return [...items, blogItem];
    }
    return items;
  }, []);
  
  // Stan lokalny nawigacji z gwarancją obecności zakładki Blog
  const [localNavItems, setLocalNavItems] = useState<NavItem[]>(() => {
    const items = getInitialNavItems();
    return ensureBlogExists(items);
  });
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isScrolled = useScrollEffect();
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Ten efekt uruchamia się przy montowaniu i zmianie ścieżki
  useEffect(() => {
    console.log('Navbar komponent zamontowany lub zmieniona ścieżka');
    
    // Zawsze pobieramy pełną listę elementów nawigacji
    const updatedNavItems = getInitialNavItems();
    
    // Zawsze upewniamy się, że Blog jest obecny
    const finalItems = ensureBlogExists(updatedNavItems);
    
    console.log('Elementy nawigacji po przywróceniu:', finalItems);
    setLocalNavItems(finalItems);
    
    // Aktualizuj kopię zapasową w window
    window._navItemsBackup = finalItems;
    
    // Logowanie stanu nawigacji do debugowania
    const blogItem = finalItems.find(item => item.href === '/blog');
    console.log('Zakładka Blog obecna w przywróconych elementach nawigacji:', !!blogItem);
  }, [location.pathname, getInitialNavItems, ensureBlogExists]);

  // Sprawdzenie bezpieczeństwa, które działa przy każdym renderowaniu
  useEffect(() => {
    // Sprawdź, czy zakładka Blog jest obecna w lokalnych elementach nawigacji
    const hasBlog = localNavItems.some(item => item.href === '/blog');
    
    if (!hasBlog) {
      console.warn('Zakładka Blog zniknęła ze stanu! Przywracam...');
      setLocalNavItems(prev => {
        const updatedItems = [...prev, { 
          label: 'Blog', 
          href: '/blog', 
          isInternal: true, 
          isPersistent: true 
        }];
        // Aktualizuj kopię zapasową
        window._navItemsBackup = updatedItems;
        return updatedItems;
      });
    }
  }, [localNavItems]);

  // Mobile menu body scroll lock
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [mobileMenuOpen]);

  // Mobile menu click-outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleNavigation = (href: string, isExternal: boolean = false) => {
    console.log('Nawigacja do:', href, 'External:', isExternal);
    
    if (isExternal) {
      window.open(href, '_blank');
      setMobileMenuOpen(false);
      return;
    }
    
    if (href.startsWith('/')) {
      try {
        // Internal routes like /blog, /portfolio, etc.
        console.log('Nawiguję do trasy:', href);
        // Force a slight delay to ensure proper context cleanup before navigation
        setTimeout(() => {
          navigate(href);
          window.scrollTo(0, 0); // Add scroll to top for internal navigation
        }, 10);
        setMobileMenuOpen(false);
      } catch (error) {
        console.error('Błąd podczas nawigacji:', error);
      }
    } else if (href.startsWith('#')) {
      // Anchor links like #about, #process, etc.
      if (location.pathname !== '/') {
        // Jeśli nie jesteśmy na stronie głównej, nawiguj do strony głównej
        // i następnie do odpowiedniej sekcji
        console.log('Nawigacja do strony głównej i sekcji:', href);
        navigate('/');
        // Opóźnienie, aby nawigacja zdążyła się zakończyć przed scrollem
        setTimeout(() => {
          const element = document.getElementById(href.substring(1));
          if (element) {
            console.log('Przewijam do elementu:', href.substring(1));
            element.scrollIntoView({ behavior: 'smooth' });
          } else {
            console.warn('Element nie znaleziony:', href.substring(1));
          }
        }, 300); // Increased timeout for more reliable navigation
      } else {
        const element = document.getElementById(href.substring(1));
        if (element) {
          console.log('Przewijam do elementu (na stronie głównej):', href.substring(1));
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          console.warn('Element nie znaleziony na stronie głównej:', href.substring(1));
        }
      }
      setMobileMenuOpen(false);
    } else {
      // External links
      window.open(href, '_blank');
      setMobileMenuOpen(false);
    }
  };

  const toggleMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  
  const handleLogoClick = () => {
    try {
      console.log('Logo clicked, navigating to home page');
      // Use setTimeout to ensure context is properly cleaned up
      setTimeout(() => {
        navigate('/');
        window.scrollTo(0, 0);
      }, 10);
    } catch (error) {
      console.error('Błąd podczas nawigacji po kliknięciu logo:', error);
    }
  };

  // Ostatnia szansa na sprawdzenie bezpieczeństwa w czasie renderowania
  // Gwarantujemy, że nawet jeśli wszystko inne zawiedzie, to zakładka Blog będzie obecna
  const renderedNavItems = useMemo(() => {
    // Jeśli z jakiegoś powodu localNavItems jest puste, użyj getInitialNavItems
    if (!localNavItems || localNavItems.length === 0) {
      console.error('KRYTYCZNE: localNavItems jest puste! Używam awaryjnej listy.');
      return ensureBlogExists(getInitialNavItems());
    }
    
    // Upewnij się, że zakładka Blog jest obecna
    return ensureBlogExists(localNavItems);
  }, [localNavItems, ensureBlogExists, getInitialNavItems]);

  // Dodatkowe sprawdzenie przed renderowaniem
  const hasBlogItem = renderedNavItems.some(item => item.href === '/blog');
  if (!hasBlogItem) {
    console.error('KRYTYCZNE: Blog nadal nieobecny w czasie renderowania!');
    // Ten kod nigdy nie powinien się wykonać dzięki useMemo powyżej, ale jest dodatkowym zabezpieczeniem
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-30 transition-all duration-300',
        isScrolled ? 'py-4 bg-white/90 backdrop-blur-md shadow-sm' : 'py-6 bg-transparent'
      )}
    >
      <div className="container-custom px-3 md:px-8 flex items-center justify-between">
        <div 
          onClick={handleLogoClick}
          className="hover:opacity-80 transition-opacity relative z-50 cursor-pointer md:ml-0 ml-2"
        >
          <Logo />
        </div>

        <DesktopNavigation 
          navItems={renderedNavItems} 
          handleNavigation={handleNavigation} 
        />

        <button
          onClick={toggleMenu}
          className="md:hidden text-elegant-900 relative z-50 focus:outline-none md:mr-0 mr-2"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <MobileMenu 
        menuRef={menuRef}
        navItems={renderedNavItems}
        mobileMenuOpen={mobileMenuOpen}
        handleNavigation={handleNavigation}
        toggleMenu={toggleMenu}
        setMobileMenuOpen={setMobileMenuOpen}
      />
    </header>
  );
};

export default Navbar;
